import {
  EuiAccordion,
  EuiBadge,
  EuiText,
  EuiTitle,
  euiPaletteColorBlind,
} from "@inscopix/ideas-eui";
import { flyoutDrsFileInfoStyles } from "./FlyoutDrsFileInfoBase";
import { capitalize, invert } from "lodash";
import { FileSource, FileStatus } from "types/constants";
import { FlyoutFileInfoBodyProps } from "./FlyoutFileInfoBody";
import { useGetDrsFileProcessingStatus } from "hooks/useGetDrsFileProcessingStatus";
import moment from "moment";
import { UserAvatarRenderer } from "components/UserAvatarRenderer/UserAvatarRenderer";
import { filesize } from "utils/filesize";
import {
  DrsFileInfoAnalysisTable,
  DrsFileInfoAnalysisTableProps,
} from "./DrsFileInfoAnalysisTable";
import { getTagsList } from "utils/getTagsList";

export type FlyoutFileInfoBodyBasicInfoProps = Pick<
  FlyoutFileInfoBodyProps,
  "activeFile" | "assignments"
> & {
  // Explicitly required | undefined because this is calculated in the parent and undefined is a valid value
  drsFileAnalysisTableInfo: DrsFileInfoAnalysisTableProps | undefined;
};

export const FlyoutFileInfoBodyBasicInfo = ({
  activeFile,
  assignments: Assignments,
  drsFileAnalysisTableInfo,
}: FlyoutFileInfoBodyBasicInfoProps) => {
  const { processingStatus } = useGetDrsFileProcessingStatus(activeFile.id);
  const tags = getTagsList(activeFile.tags, [activeFile.fileType]);

  return (
    <EuiAccordion
      id="drsFlyoutInfo"
      paddingSize="l"
      initialIsOpen={true}
      buttonContent={
        <EuiTitle size="xs">
          <h3>Info</h3>
        </EuiTitle>
      }
    >
      <div style={{ marginTop: "-16px", paddingLeft: "3px" }}>
        <EuiText size="s" css={flyoutDrsFileInfoStyles.descriptionListStyles}>
          <dl>
            <dt>Status:</dt>
            <dd>
              {processingStatus === "error" ? (
                <EuiBadge
                  color={
                    euiPaletteColorBlind({
                      rotations: 3,
                    })[17]
                  }
                >
                  Failed to read
                </EuiBadge>
              ) : (
                <>
                  {/* Converting file.status code to corresponding status string and finally capitalizing it for a nice display  */}
                  {capitalize(invert(FileStatus)[activeFile.status])}
                </>
              )}
            </dd>
            {tags.length > 0 && (
              <>
                <dt>Tags:</dt>
                <dd>
                  {tags.map((tag) => (
                    <EuiBadge key={tag} color="hollow">
                      {tag}
                    </EuiBadge>
                  ))}
                </dd>
              </>
            )}

            <dt>References:</dt>
            <dd>
              <Assignments fileId={activeFile.id} />
            </dd>

            <dt>Size:</dt>
            <dd>{filesize(activeFile.size ?? "")}</dd>

            {activeFile.source === FileSource.UPLOADED && (
              <>
                <dt>Uploaded:</dt>
                <dd>
                  {moment(activeFile.dateCreated).format(
                    "MMMM Do YYYY, h:mm:ss a",
                  )}
                </dd>

                <dt>User:</dt>
                <dd>
                  <UserAvatarRenderer
                    userId={activeFile.userId}
                    displayName={true}
                    size="s"
                  />
                </dd>
              </>
            )}
          </dl>
        </EuiText>
        {drsFileAnalysisTableInfo !== undefined && (
          <DrsFileInfoAnalysisTable {...drsFileAnalysisTableInfo} />
        )}
      </div>
    </EuiAccordion>
  );
};
